import { supabase } from './supabase';
import type { Sheet } from './store';
import type { ProjectMeta } from './types';

/**
 * Cloud project storage (Supabase `projects` table). Every call is scoped to
 * the signed-in user by Row-Level-Security, so the browser can only touch its
 * owner's rows. Run supabase/schema.sql once to create the table.
 */

/** The serialized project payload stored in the `data` jsonb column. */
export interface ProjectData {
  meta: ProjectMeta;
  sheets: Sheet[];
  activeSheetId: string;
}

/** A row as shown in the "My projects" list (without the heavy `data`). */
export interface CloudProjectRow {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

function client() {
  if (!supabase) throw new Error('Cloud storage is not configured.');
  return supabase;
}

/** Hard cap on a single project's serialized size, to protect the API request
 *  and the shared free-tier database from a runaway (image-heavy) project. */
const MAX_PROJECT_BYTES = 12_000_000;

/** Serialize + size-check a project before it goes to the cloud. Throws a
 *  user-facing message if it's too big (callers surface it as a toast). */
function encodeChecked(data: ProjectData): string {
  const json = JSON.stringify(data);
  if (json.length > MAX_PROJECT_BYTES) {
    throw new Error(
      'This project is too large to save to the cloud (max ~12 MB). Remove or shrink embedded images.',
    );
  }
  return json;
}

/** Newest-first list of the user's projects (metadata only, no `data`). */
export async function listCloudProjects(): Promise<CloudProjectRow[]> {
  const { data, error } = await client()
    .from('projects')
    .select('id, name, created_at, updated_at')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: r.id as string,
    name: (r.name as string) ?? 'Untitled project',
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  }));
}

/** Create a new project row; returns its id. */
export async function createCloudProject(name: string, data: ProjectData): Promise<string> {
  const sb = client();
  const { data: user } = await sb.auth.getUser();
  const uid = user.user?.id;
  if (!uid) throw new Error('Not signed in.');
  encodeChecked(data);
  const { data: row, error } = await sb
    .from('projects')
    .insert({ user_id: uid, name, data })
    .select('id')
    .single();
  if (error) throw error;
  return row.id as string;
}

/** Load a single project's full data. */
export async function loadCloudProject(id: string): Promise<ProjectData> {
  const { data, error } = await client()
    .from('projects')
    .select('data')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data.data as ProjectData;
}

/** Overwrite a project's data (and bump updated_at). Optionally rename. */
export async function saveCloudProject(id: string, data: ProjectData, name?: string): Promise<void> {
  encodeChecked(data);
  const patch: Record<string, unknown> = { data, updated_at: new Date().toISOString() };
  if (name !== undefined) patch.name = name;
  const { error } = await client().from('projects').update(patch).eq('id', id);
  if (error) throw error;
}

export async function renameCloudProject(id: string, name: string): Promise<void> {
  const { error } = await client()
    .from('projects')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteCloudProject(id: string): Promise<void> {
  const { error } = await client().from('projects').delete().eq('id', id);
  if (error) throw error;
}
